import { useMemo, useRef } from 'react';

import { useEnabledToolsContextProxy } from './enabled-tools-context.js';
import { ToolbarItems } from './toolbar-items.js';

import type { ComponentElement } from 'react';
import type { IToolbarItem, ToolbarItem, ToolbarItemProps } from './toolbar-item.js';

export type ToolEventHandlerFunc = (toolId: string, item: IToolbarItem) => void;
export type ToolUsedEventHandlerFunc = (toolId: string, item: IToolbarItem, eventArgs?: object) => void;

export interface IToolbarEventsHandlers {
	toolUsed: ToolUsedEventHandlerFunc;
	toolSelected: ToolEventHandlerFunc;
}

export function useToolbarFilter() {
	const proxy = useEnabledToolsContextProxy();
	const proxyRef = useRef(proxy);
	proxyRef.current = proxy;

	const toolbarEvents: IToolbarEventsHandlers = useMemo(
		() => ({
			toolUsed: (toolId: string, item: IToolbarItem, args?: object) => {
				for (const cb of proxyRef.current.onToolUsed.all()) {
					cb(toolId, item, args);
				}
			},
			toolSelected: (toolId: string, item: IToolbarItem) => {
				for (const cb of proxyRef.current.onToolSelected.all()) {
					cb(toolId, item);
				}
			},
		}),
		[],
	);

	return useMemo(
		() => ({
			toolbarEvents,
			toolbarFilter: (tool: ComponentElement<ToolbarItemProps, ToolbarItem<any>> | string) => {
				const enabledTools = proxyRef.current.enabledTools?.last() ?? Object.keys(ToolbarItems);
				const disabledTools = proxyRef.current.disabledTools?.last() ?? [];
				const filters = proxyRef.current.filterTools.all();

				const toolId = typeof tool === 'string' ? tool : (tool.type as any).ItemId;
				const enabled =
					!disabledTools.includes(toolId) &&
					enabledTools.includes(toolId) &&
					(filters.length === 0 || filters.every((f) => f(toolId)));

				return enabled ? tool : null;
			},
		}),
		[toolbarEvents],
	);
}
